/**
 * Column definitions for Stock Reconciliation list table
 */

import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Badge } from "@/components/ui/badge";
import type { StockReconciliation } from "@/lib/erp";

type ColumnFactoryOptions = {
  visibleFieldOrder?: string[];
  availableFields?: Array<{
    fieldname: string;
    label: string;
    fieldtype?: string;
  }>;
};

/**
 * Create column definitions for Stock Reconciliation list
 * Supports meta-driven list settings with visibleFieldOrder and availableFields
 */
export function createStockReconciliationColumns(
  opts: ColumnFactoryOptions = {}
): ColumnDef<StockReconciliation>[] {
  const { visibleFieldOrder, availableFields } = opts;

  // 1) Bespoke columns: nice formatting for important fields
  const baseColumns: ColumnDef<StockReconciliation>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Stock Reconciliation" />
      ),
      cell: ({ row }) => {
        const name = row.original.name;
        return <div className="font-medium">{name}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "posting_date",
      accessorKey: "posting_date",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Posting Date" />
      ),
      cell: ({ row }) => {
        const date = row.original.posting_date;
        return (
          <div>{date ? new Date(date).toLocaleDateString() : "-"}</div>
        );
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "purpose",
      accessorKey: "purpose",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Purpose" />
      ),
      cell: ({ row }) => {
        const purpose = row.original.purpose;
        return <Badge variant="outline">{purpose || "-"}</Badge>;
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: "set_warehouse",
      accessorKey: "set_warehouse",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Default Warehouse" />
      ),
      cell: ({ row }) => {
        const warehouse = row.original.set_warehouse;
        return warehouse ? <div>{warehouse}</div> : <span className="text-muted-foreground">-</span>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "docstatus",
      accessorKey: "docstatus",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Status" />
      ),
      cell: ({ row }) => {
        // Read directly from backend data (robust against list settings filtering)
        const raw = row.original.docstatus as number | string | null | undefined;
        
        // Normalize to 0 | 1 | 2
        const docstatus = raw == null ? null : (Number(raw) as 0 | 1 | 2);
        
        // Map docstatus to display text and badge variant
        const statusMap: Record<
          0 | 1 | 2,
          { text: string; variant: "default" | "secondary" | "destructive" }
        > = {
          0: { text: "Draft", variant: "secondary" },
          1: { text: "Submitted", variant: "default" },
          2: { text: "Cancelled", variant: "destructive" },
        };
        
        const { text, variant } =
          (docstatus !== null ? statusMap[docstatus] : undefined) ?? {
            text: "Unknown",
            variant: "secondary",
          };
        
        return <Badge variant={variant}>{text}</Badge>;
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: "company",
      accessorKey: "company",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Company" />
      ),
      cell: ({ row }) => {
        const company = row.original.company;
        return company ? <div>{company}</div> : <span className="text-muted-foreground">-</span>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "difference_amount",
      accessorKey: "difference_amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} label="Difference Amount" />
      ),
      cell: ({ row }) => {
        const amount = row.original.difference_amount;
        if (amount === null || amount === undefined) {
          return <span className="text-muted-foreground">-</span>;
        }
        return <div>{Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>;
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
  ];

  // If list settings not yet wired, fall back to base columns in sensible order
  if (!visibleFieldOrder || !availableFields) {
    return baseColumns;
  }

  // 2) Apply column visibility and ordering based on visibleFieldOrder
  const order = visibleFieldOrder;
  if (!order || !order.length) return baseColumns;

  // Create map of fieldname -> column
  const byId = new Map(
    baseColumns.map((col) => {
      const id = (col.id as string) || ((col as any).accessorKey as string);
      return [id, col];
    })
  );

  // Order visible columns according to visibleFieldOrder
  const ordered = order
    .map((fieldname) => byId.get(fieldname))
    .filter((col): col is ColumnDef<StockReconciliation> => !!col);

  // 3) Create generic columns for fields in visibleFieldOrder that don't have bespoke columns
  const availableFieldsMap = new Map(
    availableFields.map((f) => [f.fieldname, f])
  );

  const genericColumns: ColumnDef<StockReconciliation>[] = order
    .filter((fieldname) => !byId.has(fieldname))
    .map((fieldname) => {
      const meta = availableFieldsMap.get(fieldname);
      const label = meta?.label || fieldname;
      const fieldtype = meta?.fieldtype || "Data";

      return {
        id: fieldname,
        accessorKey: fieldname,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={label} />
        ),
        cell: ({ row }) => {
          const value = row.original[fieldname];
          
          // Generic rendering based on fieldtype
          if (value === null || value === undefined || value === "") {
            return <span className="text-muted-foreground">-</span>;
          }

          // Check field
          if (fieldtype === "Check") {
            const checked = value === 1 || value === true;
            return (
              <Badge variant={checked ? "default" : "secondary"}>
                {checked ? "Yes" : "No"}
              </Badge>
            );
          }

          // Date/Datetime fields
          if (fieldtype === "Date" || fieldtype === "Datetime") {
            try {
              const date = new Date(value as string);
              return <div>{date.toLocaleDateString()}</div>;
            } catch {
              return <div>{String(value)}</div>;
            }
          }

          // Currency/Float/Int fields
          if (fieldtype === "Currency" || fieldtype === "Float" || fieldtype === "Int") {
            const num = Number(value);
            if (isNaN(num)) return <div>{String(value)}</div>;
            if (fieldtype === "Currency") {
              return <div>{num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>;
            }
            return <div>{num.toLocaleString()}</div>;
          }

          // Link/Select/Data/Text - plain text
          return <div className="truncate max-w-[200px]">{String(value)}</div>;
        },
        enableColumnFilter: false, // v1: keep it simple
        enableSorting: true,
      } as ColumnDef<StockReconciliation>;
    });

  // 4) Extras: only truly non-configurable columns (e.g. future actions)
  // Only keep non-meta columns - meta-backed fields should only appear if in visibleFieldOrder
  const availableFieldnames = new Set(
    availableFields.map((f) => f.fieldname)
  );

  const extras = baseColumns.filter((col) => {
    const id = (col.id as string) || ((col as any).accessorKey as string);
    
    // Don't keep meta-backed fields - they should only appear if in visibleFieldOrder
    const isMetaField = availableFieldnames.has(id);
    if (isMetaField) return false;
    
    // Don't keep anything the user explicitly hid
    if (order.includes(id)) return false;
    
    // Only keep truly non-configurable columns (e.g. future "__actions" column)
    return true;
  });

  return [...ordered, ...genericColumns, ...extras];
}

